import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';
import DatePicker from '../DatePicker';
import { emptySeasonForm, formToPayload, saveErrorText } from './seasonForm';
import './seasons.css';

/**
 * Short "Create Season": name, dates, game mode, format. Every other setting starts at the same
 * defaults the old full modal had (emptySeasonForm) and is set afterwards on the season page.
 * @param {{ isOpen, onClose, onCreated: (season) => void }} props
 */
export default function CreateSeasonModal({ isOpen, onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptySeasonForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => { if (isOpen) { setForm(emptySeasonForm()); setError(''); } }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    if (!form.name.trim()) return setError(t('seasonPage.nameRequired', 'Season name is required'));
    if (!form.start_date) return setError(t('seasonPage.startRequired', 'Start date is required'));
    if (!form.end_date) return setError(t('seasonPage.endRequired', 'End date is required'));
    savingRef.current = true; setSaving(true); setError('');
    try {
      const res = await axios.post('/api/seasons', formToPayload(form));
      onCreated(res.data);
    } catch (err) {
      setError(saveErrorText(err, t));
    } finally {
      savingRef.current = false; setSaving(false);
    }
  };

  const seg = (value, options, onChange) => (
    <div className="sp-seg">
      {options.map(o => <button key={o.value} type="button" className={value === o.value ? 'active' : ''} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );

  return (
    <AnimatedModal isOpen={isOpen} onClose={() => { if (!saving) onClose(); }} title={t('seasons.createSeason', 'Create Season')} maxWidth="520px">
      <form onSubmit={submit} className="sp-create">
        <div className="sp-field">
          <label className="sp-label" htmlFor="sp-c-name">{t('createSeason.seasonName', 'Season Name')}<span className="sp-req"> *</span></label>
          <input id="sp-c-name" className="sp-input" autoFocus maxLength={100} value={form.name} placeholder={t('createSeason.seasonNamePlaceholder', 'e.g. LOT League S1')}
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="sp-field">
          <label className="sp-label">{t('createSeason.seasonDuration', 'Season Duration')}<span className="sp-req"> *</span></label>
          <div className="sp-grid2">
            <DatePicker label={t('createSeason.startDate', 'Start Date')} value={form.start_date} onChange={v => setForm(f => ({ ...f, start_date: v }))} />
            <DatePicker label={t('createSeason.endDate', 'End Date')} value={form.end_date} onChange={v => setForm(f => ({ ...f, end_date: v }))} minDate={form.start_date} />
          </div>
        </div>
        <div className="sp-grid2">
          <div className="sp-field">
            <label className="sp-label">{t('createSeason.gameMode', 'Game Mode')}</label>
            {seg(form.mode, [{ value: 'active_duty', label: 'Active Duty' }, { value: 'wingman', label: 'Wingman' }], v => setForm(f => ({ ...f, mode: v })))}
          </div>
          <div className="sp-field">
            <label className="sp-label">{t('createSeason.matchFormat', 'Match Format')}</label>
            {seg(form.best_of, [1, 3, 5].map(n => ({ value: n, label: `BO${n}` })), v => setForm(f => ({ ...f, best_of: v })))}
          </div>
        </div>
        <div className="sp-hint">{t('seasonPage.createHint', 'Rules, veto, broadcast, temp admins and integrations are set on the season page right after.')}</div>
        {error && <div className="sp-error" role="alert">{error}</div>}
        <div className="sp-create-foot">
          <button type="button" className="sp-btn ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Cancel')}</button>
          <button type="submit" className="sp-btn" disabled={saving}>{saving ? t('seasonPage.saving', 'Saving…') : t('seasonPage.createAndOpen', 'Create & open season')}</button>
        </div>
      </form>
    </AnimatedModal>
  );
}
